'use client';

import Image from 'next/image';

import { ArrowUpRight, Check } from 'lucide-react';

import { IndexRouteSchema } from '@/components/index-route-schema';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { SubpageHero } from '@/components/sections/subpage-hero';
import Testimonials from '@/components/sections/testimonials';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';
import { cn } from '@/lib/utils';

type Integration = {
  name: string;
  logo: string;
  href: string;
  tagline: string;
  accent: string;
  featured?: boolean;
};

const CONTENT = {
  en: {
    eyebrow: 'Helpdesks',
    heroTitle: 'Plug Open into the helpdesk you already use',
    heroSubtitleA: 'Zero migration. Zero seat fees.',
    heroSubtitleB: 'Live inside your existing stack in under an hour.',
    sectionHeadingA: 'Bring AI to the tools your team',
    sectionHeadingB: 'already lives in',
    sectionSub:
      'Open works as a teammate inside your helpdesk — or as a complete standalone AI helpdesk. Pick where you want to start.',
    native: 'Native',
    telephonyEyebrow: 'Telephony',
    telephonyHeadingA: 'Already have a phone system?',
    telephonyHeadingB: 'Open plugs into that too.',
    telephonySub:
      'Add voice AI to the telephony stack you already pay for — no number porting, no migration, no rebuilding your IVRs.',
    onSomethingElseA:
      'On something else? Most SIP-compatible systems work out of the box —',
    talkToUs: 'talk to us',
    integrations: {
      Open: 'The all-in-one AI helpdesk',
      Zendesk: 'Live in your Zendesk in under an hour',
      Intercom: 'The Intercom AI you were promised',
      HubSpot: 'Any tier, every feature — no suite required',
      Freshdesk: 'Real resolutions, not Freddy suggestions',
      Salesforce: 'Service Cloud — no consultants required',
      'Twilio Flex': 'The voice + chat AI Flex doesn’t have',
    } as Record<string, string>,
    promisePoints: [
      'No migration',
      'Live in under an hour',
      'Works alongside your team',
    ],
    telephony: {
      Twilio: 'CPaaS · voice + SMS',
      RingCentral: 'Cloud phone + UCaaS',
      'Zoom Phone': 'Voice for Zoom workspaces',
      Vonage: 'Global cloud communications',
      Aircall: 'Phone system for support teams',
      Dialpad: 'AI-powered cloud phone',
      Five9: 'Cloud contact center',
      Genesys: 'Enterprise CX platform',
    } as Record<string, string>,
  },
  ar: {
    eyebrow: 'أنظمة الدعم',
    heroTitle: 'وصّل Open بنظام الدعم اللي تستخدمه أصلاً',
    heroSubtitleA: 'بدون ترحيل. بدون رسوم على كل موظف.',
    heroSubtitleB: 'يعمل داخل منظومتك الحالية خلال أقل من ساعة.',
    sectionHeadingA: 'اجلب الـ AI للأدوات اللي يعيش',
    sectionHeadingB: 'فيها فريقك أصلاً',
    sectionSub:
      'يعمل Open كزميل داخل نظام الدعم اللي عندك — أو كنظام دعم ذكي متكامل قائم بذاته. اختر من وين تبدأ.',
    native: 'أصلي',
    telephonyEyebrow: 'الاتصالات',
    telephonyHeadingA: 'عندك نظام هاتف أصلاً؟',
    telephonyHeadingB: 'Open يتصل به أيضاً.',
    telephonySub:
      'أضف ذكاءً صوتياً لنظام الاتصالات اللي تدفع له أصلاً — بدون نقل أرقام، وبدون ترحيل، وبدون إعادة بناء الـ IVR.',
    onSomethingElseA:
      'عندك نظام آخر؟ أغلب الأنظمة المتوافقة مع SIP تعمل مباشرة —',
    talkToUs: 'كلّمنا',
    integrations: {
      Open: 'نظام الدعم الذكي المتكامل',
      Zendesk: 'يعمل داخل Zendesk اللي عندك خلال أقل من ساعة',
      Intercom: 'ذكاء Intercom اللي وعدوك به',
      HubSpot: 'أي باقة، كل الميزات — وبدون الحزمة الكاملة',
      Freshdesk: 'حلول تقفل التذكرة، مو اقتراحات Freddy',
      Salesforce: 'Service Cloud — بدون استشاريين',
      'Twilio Flex': 'ذكاء الصوت والمحادثة اللي Flex ما عنده',
    } as Record<string, string>,
    promisePoints: ['بدون ترحيل', 'جاهز في أقل من ساعة', 'يشتغل جنب فريقك'],
    telephony: {
      Twilio: 'CPaaS · صوت + SMS',
      RingCentral: 'هاتف سحابي + UCaaS',
      'Zoom Phone': 'صوت لفرق Zoom',
      Vonage: 'اتصالات سحابية عالمية',
      Aircall: 'نظام هاتف لفرق الدعم',
      Dialpad: 'هاتف سحابي بذكاء اصطناعي',
      Five9: 'مركز اتصال سحابي',
      Genesys: 'منصة CX للمؤسسات',
    } as Record<string, string>,
  },
} as const;

const INTEGRATIONS: Integration[] = [
  {
    name: 'Open',
    logo: '/open-logo.svg',
    href: '/helpdesks/open',
    tagline: 'The all-in-one AI helpdesk',
    accent: '#0a0a0a',
    featured: true,
  },
  {
    name: 'Zendesk',
    logo: '/helpdicks/zendesk.png',
    href: '/helpdesks/zendesk',
    tagline: 'Live in your Zendesk in under an hour',
    accent: '#03363D',
  },
  {
    name: 'Intercom',
    logo: '/helpdicks/intercom.png',
    href: '/helpdesks/intercom',
    tagline: 'The Intercom AI you were promised',
    accent: '#0A66C2',
  },
  {
    name: 'HubSpot',
    logo: '/helpdicks/hubspot.png',
    href: '/helpdesks/hubspot',
    tagline: 'Any tier, every feature — no suite required',
    accent: '#FF7A59',
  },
  {
    name: 'Freshdesk',
    logo: '/helpdicks/freshdesk.png',
    href: '/helpdesks/freshdesk',
    tagline: 'Real resolutions, not Freddy suggestions',
    accent: '#25C16F',
  },
  {
    name: 'Salesforce',
    logo: '/helpdicks/salesforce.png',
    href: '/helpdesks/salesforce',
    tagline: 'Service Cloud — no consultants required',
    accent: '#00A1E0',
  },
  {
    name: 'Twilio Flex',
    logo: '/helpdicks/flex.png',
    href: '/helpdesks/twilio-flex',
    tagline: 'The voice + chat AI Flex doesn’t have',
    accent: '#F22F46',
  },
];

const TELEPHONY: Integration[] = [
  {
    name: 'Twilio',
    logo: '/telephony/twilio.svg',
    href: '/helpdesks/twilio-flex',
    tagline: 'CPaaS · voice + SMS',
    accent: '#F22F46',
  },
  {
    name: 'RingCentral',
    logo: '/telephony/ringcentral.svg',
    href: '/channels/voice',
    tagline: 'Cloud phone + UCaaS',
    accent: '#FF8800',
  },
  {
    name: 'Zoom Phone',
    logo: '/telephony/zoom.svg',
    href: '/channels/voice',
    tagline: 'Voice for Zoom workspaces',
    accent: '#0B5CFF',
  },
  {
    name: 'Vonage',
    logo: '/telephony/vonage.svg',
    href: '/channels/voice',
    tagline: 'Global cloud communications',
    accent: '#0F0F0F',
  },
  {
    name: 'Aircall',
    logo: '/telephony/aircall.svg',
    href: '/channels/voice',
    tagline: 'Phone system for support teams',
    accent: '#00B388',
  },
  {
    name: 'Dialpad',
    logo: '/telephony/dialpad.svg',
    href: '/channels/voice',
    tagline: 'AI-powered cloud phone',
    accent: '#7C3AED',
  },
  {
    name: 'Five9',
    logo: '/telephony/five9.svg',
    href: '/channels/voice',
    tagline: 'Cloud contact center',
    accent: '#0066A1',
  },
  {
    name: 'Genesys',
    logo: '/telephony/genesys.svg',
    href: '/channels/voice',
    tagline: 'Enterprise CX platform',
    accent: '#FF4F1F',
  },
];

export default function HelpdeskDirectoryPage() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;

  return (
    <>
      <IndexRouteSchema route="/helpdesks" />
      <SubpageHero
        eyebrow={c.eyebrow}
        title={c.heroTitle}
        subtitle={
          <>
            {c.heroSubtitleA}
            <br />
            {c.heroSubtitleB}
          </>
        }
        worksWith={null}
      />

      <section className="bg-white py-24 md:py-32">
        <div className="container">
          <div className="mx-auto mb-12 flex max-w-3xl flex-col items-center gap-4 text-center md:mb-16">
            <p className="text-xs font-medium tracking-widest text-neutral-500 uppercase">
              {t.helpdesks['Choose your helpdesk']}
            </p>
            <h2 className="font-serif text-2xl text-neutral-900 md:text-4xl">
              {c.sectionHeadingA}{' '}
              <span className="text-balance">{c.sectionHeadingB}</span>
            </h2>
            <p className="mt-1 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">
              {c.sectionSub}
            </p>

            <ul className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-neutral-600">
              {c.promisePoints.map((point) => (
                <li key={point} className="flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-emerald-500" />
                  {point}
                </li>
              ))}
            </ul>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {INTEGRATIONS.map((item) => (
              <IntegrationCard
                key={item.name}
                integration={item}
                tagline={c.integrations[item.name] ?? item.tagline}
                nativeLabel={c.native}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#fafaf9] py-24 md:py-32">
        <div className="container">
          <div className="mx-auto mb-12 flex max-w-3xl flex-col items-center gap-4 text-center md:mb-16">
            <p className="text-xs font-medium tracking-widest text-neutral-500 uppercase">
              {c.telephonyEyebrow}
            </p>
            <h2 className="font-serif text-2xl text-neutral-900 md:text-4xl">
              {c.telephonyHeadingA}{' '}
              <span className="text-balance">{c.telephonyHeadingB}</span>
            </h2>
            <p className="mt-1 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">
              {c.telephonySub}
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {TELEPHONY.map((item) => (
              <IntegrationCard
                key={item.name}
                integration={item}
                tagline={c.telephony[item.name] ?? item.tagline}
                nativeLabel={c.native}
              />
            ))}
          </div>

          <p className="mt-10 text-center text-sm text-neutral-500">
            {c.onSomethingElseA}{' '}
            <Link
              href="/demo"
              aria-label={t.common['Learn more about Open and book a demo']}
              className="underline underline-offset-4 hover:text-neutral-900"
            >
              {c.talkToUs}
            </Link>
            .
          </p>
        </div>
      </section>

      <CustomerVideos />

      <Testimonials />
    </>
  );
}

const IntegrationCard = ({
  integration,
  tagline,
  nativeLabel,
}: {
  integration: Integration;
  tagline: string;
  nativeLabel: string;
}) => {
  const { name, logo, href, accent, featured } = integration;
  return (
    <Link
      href={href}
      className={cn(
        'group relative flex flex-col gap-5 overflow-hidden rounded-2xl border bg-white p-6 transition-all duration-300',
        'hover:-translate-y-0.5',
        featured
          ? 'border-neutral-900/20 ring-1 ring-neutral-900/10'
          : 'border-neutral-200 hover:border-neutral-300',
      )}
    >
      {/* Soft brand-color wash on hover */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{
          background: `linear-gradient(135deg, ${accent}10 0%, transparent 60%)`,
        }}
      />

      <div className="relative flex items-start justify-between">
        <div
          className="flex h-12 w-12 items-center justify-center rounded-xl border border-neutral-200 bg-white p-2"
          style={{ boxShadow: `0 0 0 0 ${accent}` }}
        >
          <Image
            src={logo}
            alt={name}
            width={32}
            height={32}
            className="h-full w-full object-contain"
          />
        </div>
        <ArrowUpRight className="h-5 w-5 text-neutral-300 transition-all duration-200 group-hover:text-neutral-900 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </div>

      <div className="relative">
        <div className="flex items-center gap-2">
          <h3 className="text-base text-neutral-900">{name}</h3>
          {featured && (
            <span className="rounded-full bg-neutral-900 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white">
              {nativeLabel}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm leading-snug text-neutral-500">{tagline}</p>
      </div>
    </Link>
  );
};
